import { useState, Fragment } from "react";
import {
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  PlusIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { Dialog, Transition } from "@headlessui/react";
import {
  useUsersQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useCreateUserMutation,
} from "../../../hooks/useUsersQuery";
import Loader from "../../../components/Loader/Loader";
import { toast } from "react-toastify";
import { Link } from "@tanstack/react-router";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useUser } from "../../../context/UserContext";

const UserSettings = () => {
  // Use TanStack Query with real-time updates
  const { data: users = [], isLoading, error } = useUsersQuery();
  const updateUserMutation = useUpdateUserMutation();
  const deleteUserMutation = useDeleteUserMutation();
  const createUserMutation = useCreateUserMutation();
  const { user: currentUser } = useUser();

  const [searchTerm, setSearchTerm] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    role: "user",
  });

  const [createForm, setCreateForm] = useState({
    fullname: "",
    email: "",
    password: "",
    role: "user",
  });

  // Handle query error
  if (error) {
    toast.error("Σφάλμα κατά την ανάκτηση των χρηστών");
  }

  const filteredUsers =
    searchTerm.trim() === ""
      ? users // If no search term, show all users
      : users.filter(
          (user) =>
            (user.name &&
              user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (user.fullname &&
              user.fullname.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (user.email &&
              user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (user.role &&
              user.role.toLowerCase().includes(searchTerm.toLowerCase())),
        );

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.fullname || user.name || "",
      email: user.email || "",
      role: user.role || "user",
    });
    setShowEditModal(true);
  };

  const handleCreateUser = () => {
    setCreateForm({
      fullname: "",
      email: "",
      password: "",
      role: "user",
    });
    setShowCreateModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) {
      toast.error("Το όνομα είναι υποχρεωτικό");
      return;
    }

    try {
      // Prepare the data to save, using fullname instead of name
      const updateData = {
        fullname: editForm.name,
        role: editForm.role,
      };

      await updateUserMutation.mutateAsync({
        userId: editingUser.id,
        userData: updateData,
      });
      setShowEditModal(false);
      setEditingUser(null);
      // TanStack Query will automatically update the cache via real-time listener
    } catch (error) {
      // Error handling is done in the mutation
      console.error("Failed to update user:", error);
    }
  };

  const handleSaveCreate = async () => {
    if (
      !createForm.fullname.trim() ||
      !createForm.email.trim() ||
      !createForm.password.trim()
    ) {
      toast.error("Όλα τα πεδία είναι υποχρεωτικά");
      return;
    }

    if (createForm.password.length < 6) {
      toast.error("Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες");
      return;
    }

    try {
      const userDataWithCongregation = {
        ...createForm,
        congregation: currentUser?.congregation || null
      };
      
      await createUserMutation.mutateAsync(userDataWithCongregation);
      setShowCreateModal(false);
      setCreateForm({
        fullname: "",
        email: "",
        password: "",
        role: "user",
      });
      // TanStack Query will automatically update the cache via real-time listener
    } catch (error) {
      // Error handling is done in the mutation
      console.error("Failed to create user:", error);
    }
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteUserMutation.mutateAsync(userToDelete.id);
      setShowDeleteModal(false);
      setUserToDelete(null);
      // TanStack Query will automatically update the cache via real-time listener
    } catch (error) {
      // Error handling is done in the mutation
      console.error("Failed to delete user:", error);
    }
  };

  const getRoleBadge = (role) => {
    return role === "admin" ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        Διαχειριστής
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Χρήστης
      </span>
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="md:container mx-auto flex flex-col py-4 justify-center">
      {/* Back Navigation */}
      <Link
        to="/dashboard"
        className="p-2 inline-flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
      >
        <ArrowLeftIcon className={"size-5"} /> Πίσω
      </Link>

      {/* Header */}
      <div className="bg-white shadow-lg border rounded-2xl mx-2 mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gray-800 p-2 rounded-lg">
                <UserCircleIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="md:text-2xl font-bold text-gray-900">
                  Διαχείριση Χρηστών
                </h1>
                <p className="text-sm text-gray-500">
                  Σύνολο χρηστών: {users.length}
                </p>
              </div>
            </div>
            <button
              onClick={handleCreateUser}
              className="inline-flex text-sm items-center px-4 py-2 bg-gray-900 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Νέος
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Αναζήτηση χρηστών (όνομα, email, ρόλος)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-gray-800 focus:border-transparent"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="overflow-hidden">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <UserCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {searchTerm ? "Δεν βρέθηκαν χρήστες" : "Δεν υπάρχουν χρήστες"}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm
                  ? "Δοκιμάστε διαφορετικούς όρους αναζήτησης"
                  : "Δεν έχουν καταχωρηθεί χρήστες ακόμα"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Χρήστης
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ειδοποιήσεις
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ρόλος
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ενέργειες
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user, index) => (
                    <tr
                      key={user.id}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full flex items-center justify-center">
                              <UserCircleIcon className="w-5 h-5 text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-2">
                            <div className="text-sm font-medium text-gray-900">
                              {user.fullname || user.name || "Χωρίς όνομα"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {user.email || "Χωρίς email"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center">
                          {user.firebaseToken &&
                          user.firebaseToken.trim() !== "" ? (
                            <div className="flex items-center text-green-600">
                              <CheckCircleIcon className="w-5 h-5 mr-2" />
                            </div>
                          ) : (
                            <div className="flex items-center text-red-600">
                              <XCircleIcon className="w-5 h-5 mr-2" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getRoleBadge(user.role)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="bg-gray-900 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
                            title="Επεξεργασία χρήστη"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
                            title="Διαγραφή χρήστη"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit User Modal */}
      <Transition.Root show={showEditModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setShowEditModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="p-6">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="bg-gray-900 p-2 rounded-lg">
                        <PencilSquareIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          Επεξεργασία Χρήστη
                        </h3>
                        <p className="text-sm text-gray-500">
                          Ενημερώστε τα στοιχεία του χρήστη
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Όνομα *
                        </label>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) =>
                            setEditForm({ ...editForm, name: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                          placeholder="Εισάγετε το όνομα"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) =>
                            setEditForm({ ...editForm, email: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent bg-gray-50"
                          placeholder="Email χρήστη"
                          disabled
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Το email δεν μπορεί να αλλάξει
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Ρόλος
                        </label>
                        <select
                          value={editForm.role}
                          onChange={(e) =>
                            setEditForm({ ...editForm, role: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                        >
                          <option value="user">Χρήστης</option>
                          <option value="admin">Διαχειριστής</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => setShowEditModal(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                        Ακύρωση
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        disabled={updateUserMutation.isPending}
                        className="px-4 py-2 text-sm font-medium text-white bg-gray-900 border border-transparent rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updateUserMutation.isPending
                          ? "Αποθηκεύεται..."
                          : "Αποθήκευση"}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Delete Confirmation Modal */}
      <Transition.Root show={showDeleteModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setShowDeleteModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="p-6">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="bg-red-600 p-2 rounded-lg">
                        <TrashIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          Επιβεβαίωση Διαγραφής
                        </h3>
                        <p className="text-sm text-gray-500">
                          Αυτή η ενέργεια δεν μπορεί να αναιρεθεί
                        </p>
                      </div>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                      <p className="text-sm text-red-800">
                        Είστε σίγουροι ότι θέλετε να διαγράψετε τον χρήστη{" "}
                        <strong>
                          {userToDelete?.fullname ||
                            userToDelete?.name ||
                            "χωρίς όνομα"}
                        </strong>
                        ;
                        <br />
                        <span className="text-red-600">
                          Όλα τα δεδομένα του χρήστη θα διαγραφούν οριστικά.
                        </span>
                      </p>
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => setShowDeleteModal(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                        Ακύρωση
                      </button>
                      <button
                        onClick={confirmDelete}
                        disabled={deleteUserMutation.isPending}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {deleteUserMutation.isPending
                          ? "Διαγράφεται..."
                          : "Διαγραφή"}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Create User Modal */}
      <Transition.Root show={showCreateModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setShowCreateModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="p-6">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="bg-gray-900 p-2 rounded-lg">
                        <PlusIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          Δημιουργία Νέου Χρήστη
                        </h3>
                        <p className="text-sm text-gray-500">
                          Εισάγετε τα στοιχεία του νέου χρήστη
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Πλήρες Όνομα *
                        </label>
                        <input
                          type="text"
                          value={createForm.fullname}
                          onChange={(e) =>
                            setCreateForm({
                              ...createForm,
                              fullname: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                          placeholder="Εισάγετε το πλήρες όνομα"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={createForm.email}
                          onChange={(e) =>
                            setCreateForm({
                              ...createForm,
                              email: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                          placeholder="Εισάγετε το email"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Κωδικός Πρόσβασης *
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={createForm.password}
                            onChange={(e) =>
                              setCreateForm({
                                ...createForm,
                                password: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                            placeholder="Εισάγετε τον κωδικό (τουλάχιστον 6 χαρακτήρες)"
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                            ) : (
                              <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Ρόλος
                        </label>
                        <select
                          value={createForm.role}
                          onChange={(e) =>
                            setCreateForm({
                              ...createForm,
                              role: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                        >
                          <option value="user">Χρήστης</option>
                          <option value="admin">Διαχειριστής</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => setShowCreateModal(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                        Ακύρωση
                      </button>
                      <button
                        onClick={handleSaveCreate}
                        disabled={createUserMutation.isPending}
                        className="px-4 py-2 text-sm font-medium text-white bg-gray-900 border border-transparent rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {createUserMutation.isPending
                          ? "Δημιουργείται..."
                          : "Δημιουργία"}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default UserSettings;
